/**
 * @quotech/ui — Badge
 *
 * Small rounded label primitive. Tone-mapped colours mirror the palette
 * used by StatusBadge so neutral/success/warning/danger/info read the
 * same across the app in both light and dark mode.
 *
 * FLOW: props (tone + native span attrs) → cn() class merge → <span>
 *
 * USAGE:
 *   import { Badge } from '@quotech/ui'
 *   <Badge tone="success">Paid</Badge>
 *
 * NOTE: a richer shadcn/cva Badge lives at ./ui/badge (internal, not
 * exported from the package barrel). This is the lightweight public
 * primitive for the P2 migration.
 */
import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '../lib/utils'

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
}

const TONE_STYLES: Record<BadgeTone, string> = {
  neutral: 'bg-secondary text-muted-foreground border-border',
  success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  danger: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  info: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone = 'neutral', ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
        TONE_STYLES[tone],
        className,
      )}
      {...props}
    />
  ),
)
Badge.displayName = 'Badge'
